import { GraniteButton } from 'components/V2/Button/GraniteButton';
import {
  BuildOutline,
  CaretDown,
  CaretUp,
  HelpCircleOutline,
  PulseOutline,
  ReceiptOutline,
  SpeedometerOutline,
  TimeOutline,
  Wifi,
} from 'react-ionicons';
import { motion } from 'framer-motion';
import { FC, useMemo, useState } from 'react';
import { InvoiceComparisonQuotes } from 'api/orders/schemas';
import { formatCurrency } from 'shared/util/formatCurrency';
import { formatBandwidth, formatTerm } from 'screens/SelectServices/utils';
import { getIpTypeLabel } from 'screens/OpenQuote/utils';
import { ProductOfferingType } from 'screens/SelectServices/schemas';
import {
  Tooltip,
  useTooltip,
} from 'screens/AccessExpressV3/AccessProductDetails/NetworkMonitoringLabel';
import LocationIcon from 'screens/AccessExpressV3/BaseComponents/LocationIcon';
import { formatAddress } from './utils';

const variants = {
  expanded: {
    height: 'auto',
    opacity: 1,
    transition: {
      type: 'spring',
      mass: 1,
      stiffness: 100,
      damping: 15,
    },
  },
  collapsed: {
    height: 0,
    opacity: 0,
    transition: {
      type: 'spring',
      mass: 1,
      stiffness: 100,
      damping: 15,
    },
  },
};

export const ComparisonQuoteBody = ({
  data,
}: {
  data: InvoiceComparisonQuotes;
}) => {
  const [seeMore, setSeeMore] = useState(false);
  const onSeeMore = () => setSeeMore((prev) => !prev);

  const {
    location,
    bestOffer,
    currentOffer,

    totalMRC,
    speed,
    provider,
    term,
    ipType,
  } = useMemo(() => {
    const [location] = data?.granite_quote?.locations ?? [];
    const [bestOffer] = location?.best_offerings ?? [];
    const currentOffer = data?.original_quote;

    if (!location || !bestOffer || !currentOffer) {
      return {
        location: null,
        bestOffer: null,
        currentOffer: null,
        product: null,
        pricing: null,
        totalMRC: 0,
        speed: '',
        provider: '',
        term: '',
        ipType: '',
      };
    }

    const product =
      bestOffer.formal_broadband_product ?? bestOffer.formal_dia_product;

    const pricing =
      product?.formal_broadband_pricing ?? product?.formal_dia_pricing;

    const totalMRC =
      (pricing?.total_mrc ?? 0) +
      (product?.mns_essential
        ? pricing?.mns_essentials ?? 0
        : product?.mns_enhanced
          ? pricing?.mns_enhanced ?? 0
          : 0);

    const speed = `${formatBandwidth(product?.bandwidth_download ?? '0 MBPS')}`;
    const provider = bestOffer.provider ?? '';
    const term = formatTerm(bestOffer.term);
    const ipType = getIpTypeLabel((product?.ip_type ?? '') as string);

    return {
      location,
      bestOffer,
      currentOffer,
      product,
      pricing,
      totalMRC,
      speed,
      provider,
      term,
      ipType,
    };
  }, [data]);

  return (
    <div className="flex flex-col gap-4">
      {location && (
        <div className="m-auto flex items-center gap-2">
          <LocationIcon width="24px" height="24px" color="#9796F3" />
          <span className="w-full text-start text-base font-bold text-content-base-default">
            {formatAddress(location)}
          </span>
        </div>
      )}
      <div className="max-w-[452px] rounded bg-[linear-gradient(103deg,_#7E77B3_7.1%,_#8989FF_27.23%,_#6C9DFF_48.11%,_#78CFDA_69.15%,_#9FF4A9_90.11%)] p-0.5 shadow-[0px_4px_4px_0px_rgba(0,_0,_0,_0.25)]">
        <div className="relative z-10 bg-background-base-surface-2">
          <div className="flex flex-col rounded bg-[rgba(43,_43,_63,_0.4)] px-8 pb-4 pt-8">
            <p className="flex flex-col items-center justify-between gap-4 text-2xl font-bold text-purple-300 sm:flex-row">
              <span className="mr-7 whitespace-nowrap">Granite offer</span>
              <span className="font-semibold">
                {formatCurrency(totalMRC)}/month
              </span>
            </p>
            <div className="my-4 h-[1px] w-full bg-white" />
            <div className="grid gap-2 xs:grid-cols-2">
              <p className="flex items-center gap-1 text-sm font-bold text-white">
                <SpeedometerOutline
                  width="16px"
                  height="16px"
                  color="currentColor"
                />
                {provider} {speed}
              </p>
              <p className="flex items-center gap-1 text-sm font-bold text-white">
                <TimeOutline width="16px" height="16px" color="currentColor" />
                28 day install avg
              </p>
              <p className="flex items-center gap-1 text-sm font-bold text-white">
                <ReceiptOutline
                  width="16px"
                  height="16px"
                  color="currentColor"
                />
                {term}
              </p>
              <p className="flex items-center gap-1 text-sm font-bold text-white">
                <Wifi width="16px" height="16px" color="currentColor" />
                {ipType}
              </p>
              <p className="flex items-center gap-1 text-sm font-bold text-white">
                <BuildOutline width="16px" height="16px" color="currentColor" />
                $0 est.construction
              </p>
              {/* <p className="flex items-center gap-1 text-sm font-bold text-white">
                <PulseOutline width="16px" height="16px" color="currentColor" />
                Network monitoring
              </p> */}
            </div>
            <div className="mx-auto mt-4">
              <GraniteButton variant="ghost" size="small" onClick={onSeeMore}>
                See more
                {seeMore ? (
                  <CaretUp color="currentColor" width="18px" height="18px" />
                ) : (
                  <CaretDown color="currentColor" width="18px" height="18px" />
                )}
              </GraniteButton>
            </div>
            <motion.div
              variants={variants}
              initial="collapsed"
              animate={seeMore ? 'expanded' : 'collapsed'}
              style={{ overflow: 'hidden' }}
            >
              <ComparisonQuoteChargeDetails
                bestOffer={bestOffer as ProductOfferingType}
              />
            </motion.div>
          </div>
        </div>
      </div>
      <CurrentServiceBody
        currentService={
          currentOffer as CurrentServiceBodyProps['currentService']
        }
      />
    </div>
  );
};

const ComparisonQuoteChargeDetails = ({
  bestOffer,
}: {
  bestOffer: ProductOfferingType;
}) => {
  const pricing =
    bestOffer?.formal_broadband_product?.formal_broadband_pricing ??
    bestOffer?.formal_dia_product?.formal_dia_pricing;

  return (
    <div className="mt-4 space-y-4 pb-4">
      <ChargeDetails
        title="Monthly recurring charges"
        details={[
          { title: 'Connection', desc: formatCurrency(pricing?.circuit) },
          ...(bestOffer?.formal_broadband_product?.formal_broadband_pricing
            ? [
                {
                  title: 'Internet Access Recovery',
                  //@ts-expect-error err
                  desc: formatCurrency(pricing?.internet_access_recovery),
                },
              ]
            : []),
          {
            title: 'Router',
            desc: formatCurrency(pricing?.modem || pricing?.equipment),
          },
          { title: 'IP blocks', desc: formatCurrency(pricing?.ip) },
          // {
          //   title: 'Network monitoring',
          //   desc: formatCurrency(
          //     product?.mns_essential && pricing && 'mns_essentials' in pricing
          //       ? pricing.mns_essentials || 0
          //       : product?.mns_enhanced && pricing && 'mns_enhanced' in pricing
          //         ? pricing.mns_enhanced || 0
          //         : 0,
          //   ),
          //   tooltip: product?.mns_essential ? (
          //     <>
          //       Granite’s <b>MNS Essentials</b> bundle provides end-to-end
          //       network management. Includes ping monitoring for basic network
          //       performance insights and the edgeboot service bundle.
          //     </>
          //   ) : product?.mns_enhanced ? (
          //     <>
          //       Granite’s <b>MNS Enhanced</b> bundle provides end-to-end network
          //       management. Includes advanced network telemetry for detailed
          //       performance insights, API access, and the edgeboot service
          //       bundle.
          //     </>
          //   ) : null,
          // },
        ]}
      />
      <ChargeDetails
        title="Non-recurring charges"
        details={[
          {
            title: 'Service activation fee',
            desc: formatCurrency(pricing?.service_activation ?? 0),
          },
          // { title: 'Install fee', desc: '$150' },
          // {
          //   title: 'Estimated wiring charges',
          //   desc: '$450',
          //   tooltip: 'tooltip',
          // },
        ]}
      />
    </div>
  );
};

// On click
// Change to: "Expanded";
// Animate: Smart animate;
// Spring: { mass: 1, stiffness: 100, damping: 15 };

interface ChargeDetailsProps {
  title: string;
  details: { title: string; desc: string; tooltip?: React.ReactNode }[];
}
const ChargeDetails: FC<ChargeDetailsProps> = ({ title, details }) => {
  const { triggerEltRef, isVisible, handleEnter, handleLeave } = useTooltip();

  return (
    <div>
      <p className="text-xs font-bold text-white">{title}</p>
      <div className="mt-0.5 flex flex-col gap-0.5">
        {details.map((el, i) => (
          <p
            key={i}
            className="flex justify-between text-xs font-semibold"
            onMouseEnter={() =>
              el.title === 'Network monitoring' ? handleEnter() : {}
            }
            onMouseLeave={() =>
              el.title === 'Network monitoring' ? handleLeave() : {}
            }
          >
            <span className="flex justify-center gap-1">
              {el.title}{' '}
              {el.tooltip ? (
                <span ref={triggerEltRef}>
                  <HelpCircleOutline
                    width="16px"
                    height="16px"
                    color="currentColor"
                  />
                </span>
              ) : null}
            </span>{' '}
            <span className="text-white">{el.desc}</span>
            <Tooltip
              triggerEltRef={triggerEltRef}
              isVisible={isVisible}
              title="Network monitoring"
              body={el.tooltip}
            />
          </p>
        ))}
      </div>
    </div>
  );
};

interface CurrentServiceBodyProps {
  currentService: {
    total_mrc?: number | null;
    download_speed?: string | null;
    static_ip?: boolean | null;
    network_monitoring?: boolean | null;
  };
}

export const CurrentServiceBody: FC<CurrentServiceBodyProps> = ({
  currentService,
}) => {
  const [seeMore, setSeeMore] = useState(false);
  const onSeeMore = () => setSeeMore((prev) => !prev);

  return (
    <div className="px-8 py-4 pt-0">
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        <p className="text-xl font-bold text-white">Current service</p>
        <GraniteButton
          variant="ghost"
          size="small"
          onClick={onSeeMore}
          className="!text-xl !font-semibold"
        >
          {formatCurrency(currentService?.total_mrc ?? 0)}/month
          {seeMore ? (
            <CaretUp color="currentColor" width="12px" height="12px" />
          ) : (
            <CaretDown color="currentColor" width="12px" height="12px" />
          )}
        </GraniteButton>
      </div>
      <motion.div
        variants={variants}
        initial="collapsed"
        animate={seeMore ? 'expanded' : 'collapsed'}
        style={{ overflow: 'hidden' }}
      >
        <div className="my-4 h-[1px] w-full bg-white" />
        <div className="flex flex-col justify-between gap-1 xs:flex-row">
          <p className="flex items-center gap-1 text-xs font-semibold text-white">
            <SpeedometerOutline
              width="12px"
              height="12px"
              color="currentColor"
            />
            {formatBandwidth(currentService?.download_speed ?? '0 MBPS')}
          </p>
          <p className="flex items-center gap-1 text-xs font-semibold text-white">
            <Wifi width="12px" height="12px" color="currentColor" />
            {currentService?.static_ip ? 'Static IP' : 'Dynamic IP'}
          </p>
          {currentService?.network_monitoring && (
            <p className="flex items-center gap-1 text-xs font-semibold text-white">
              <PulseOutline width="12px" height="12px" color="currentColor" />
              Network monitoring
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};
